import React, { useState, useEffect, useMemo, useCallback } from "react";
// import Loader from "../../loader/Loader";
import { toast } from "react-toastify";
import axios from "axios";
import { domain } from "../../../security";
import { useSelector } from "react-redux";
import { selectUserID } from "../../../redux/IchthusSlice";

// Icons
import {
  Search,
  Warehouse,
  ChevronDown,
  Loader2,
  AlertCircle,
  Server,
} from "lucide-react";
import {
  FaTimes,
  FaSave,
  FaArrowRight,
  FaCheck,
  FaBoxOpen,
  FaMinus,
  FaTrash,
} from "react-icons/fa";
import { ImPlus } from "react-icons/im";

const inputClass =
  "block w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm font-semibold text-gray-900 transition-all focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-200 outline-none";
const labelClass =
  "mb-1 ml-0.5 block text-[10px] font-black uppercase tracking-widest text-gray-500";

const StepIndicator = ({ step, isEditMode }) => {
  return (
    <div className="flex items-center gap-2">
      {[
        { n: 1, label: "Select Source & Products" },
        { n: 2, label: "Select Destination" },
      ].map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black transition-all ${
                step === s.n
                  ? "bg-indigo-600 text-white shadow-lg shadow-indigo-200"
                  : step > s.n
                    ? "bg-green-500 text-white"
                    : "bg-gray-200 text-gray-400"
              }`}
            >
              {step > s.n ? <FaCheck size={9} /> : s.n}
            </div>
            <span
              className={`text-xs font-bold hidden sm:inline transition-colors ${
                step === s.n
                  ? "text-indigo-600"
                  : step > s.n
                    ? "text-green-600"
                    : "text-gray-400"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < 1 && (
            <div
              className={`w-8 h-0.5 rounded-full transition-colors ${
                step > 1 ? "bg-green-400" : "bg-gray-200"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// --- MOBILE PRODUCT CARD (Fix for Issue 2) ---
const MobileProductCard = ({
  group,
  selectedItems,
  onAdd,
  onRemove,
  activeSourceId,
}) => {
  const [selectedVariantId, setSelectedVariantId] = useState(group[0].uniqueId);
  const activeItem =
    group.find((i) => i.uniqueId === selectedVariantId) || group[0];

  const cartKey = `${activeItem.uniqueId}-${activeSourceId}`;
  const qtyInSelection =
    selectedItems.find((s) => s.cartKey === cartKey)?.quantity || 0;

  const isOutOfStock =
    activeItem.stockCount != null && activeItem.stockCount <= 0;
  const canAdd =
    !isOutOfStock &&
    (activeItem.stockCount == null || qtyInSelection < activeItem.stockCount);

  return (
    <div
      className={`p-4 bg-white rounded-xl border ${isOutOfStock ? "border-red-200 bg-red-50/20" : "border-gray-200"} shadow-sm flex flex-col gap-3`}
    >
      <div className="flex justify-between items-start">
        <div>
          <p className="font-bold text-gray-800 text-sm leading-tight">
            {activeItem.productName}
          </p>
          <p className="text-gray-400 text-[10px] font-mono mt-0.5">
            {activeItem.itemCode || "N/A"}
          </p>
        </div>
        <div className="text-right shrink-0">
          {isOutOfStock ? (
            <span className="text-red-500 font-semibold text-[11px]">
              Out of Stock
            </span>
          ) : (
            <span className="text-gray-600 font-bold bg-gray-100 px-2 py-0.5 rounded text-[11px]">
              Stock: {activeItem.stockCount ?? "—"}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
        {group.length === 1 ? (
          <span className="text-gray-600 font-medium text-xs bg-gray-50 px-2 py-1 rounded border border-gray-100">
            {activeItem.uomName}
          </span>
        ) : (
          <select
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-indigo-400 bg-white"
            value={activeItem.uniqueId}
            onChange={(e) => setSelectedVariantId(e.target.value)}
          >
            {group.map((v) => (
              <option key={v.uniqueId} value={v.uniqueId}>
                {v.uomName}
              </option>
            ))}
          </select>
        )}
        <div>
          {qtyInSelection > 0 ? (
            <div className="flex items-center gap-2 bg-indigo-50 rounded-lg p-1">
              <button
                onClick={() => onRemove(activeItem)}
                className="w-7 h-7 bg-white hover:bg-red-50 hover:text-red-600 text-gray-600 rounded-md flex items-center justify-center shadow-sm"
              >
                <FaMinus size={10} />
              </button>
              <span className="text-sm font-black text-indigo-700 w-6 text-center">
                {qtyInSelection}
              </span>
              <button
                onClick={() => canAdd && onAdd(activeItem)}
                disabled={!canAdd}
                className="w-7 h-7 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md flex items-center justify-center shadow-sm disabled:opacity-40"
              >
                <ImPlus size={10} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => canAdd && onAdd(activeItem)}
              disabled={!canAdd}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                !canAdd
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 active:scale-95"
              }`}
            >
              Add +
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// --- DESKTOP PRODUCT ROW ---
const ProductRow = ({
  group,
  selectedItems,
  onAdd,
  onRemove,
  activeSourceId,
}) => {
  const [selectedVariantId, setSelectedVariantId] = useState(group[0].uniqueId);
  const activeItem =
    group.find((i) => i.uniqueId === selectedVariantId) || group[0];

  const cartKey = `${activeItem.uniqueId}-${activeSourceId}`;
  const qtyInSelection =
    selectedItems.find((s) => s.cartKey === cartKey)?.quantity || 0;

  const isOutOfStock =
    activeItem.stockCount != null && activeItem.stockCount <= 0;
  const canAdd =
    !isOutOfStock &&
    (activeItem.stockCount == null || qtyInSelection < activeItem.stockCount);

  return (
    <tr
      className={`border-b border-gray-100 hover:bg-gray-50/50 transition-colors ${isOutOfStock ? "bg-red-50/20" : ""}`}
    >
      <td className="py-2 px-6">
        <p className="font-bold text-gray-800 text-sm">
          {activeItem.productName}
        </p>
        <p className="text-gray-400 text-[10px] font-mono">
          {activeItem.itemCode || "N/A"}
        </p>
      </td>
      <td className="py-2 px-6 text-center">
        {activeItem.brandName ?
        <>
          <span className="text-xs">{activeItem.brandName}</span>
        </>
        :
        <>
          <span className="text-xs italic text-gray-400">{"N/A"}</span>
        </>
        }
      </td>
      <td className="py-2 px-6 text-center">
        {group.length === 1 ? (
          <span className="text-gray-600 font-medium text-xs">
            {activeItem.uomName}
          </span>
        ) : (
          <select
            className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:border-indigo-400 bg-white cursor-pointer"
            value={activeItem.uniqueId}
            onChange={(e) => setSelectedVariantId(e.target.value)}
          >
            {group.map((v) => (
              <option key={v.uniqueId} value={v.uniqueId}>
                {v.uomName}
              </option>
            ))}
          </select>
        )}
      </td>
      <td className="py-2 px-6 text-center">
        {isOutOfStock ? (
          <span className="text-red-500 font-semibold text-[11px]">
            Out of Stock
          </span>
        ) : (
          <span className="text-gray-600 font-bold bg-gray-100 px-2 py-0.5 rounded text-[11px]">
            {activeItem.stockCount ?? "—"}
          </span>
        )}
      </td>
      <td className="py-2 px-6 text-center">
        {qtyInSelection > 0 ? (
          <>
            <div className="hidden items-center justify-center gap-1.5">
              <button
                onClick={() => onRemove(activeItem)}
                className="w-6 h-6 bg-gray-100 hover:bg-red-100 hover:text-red-600 text-gray-500 rounded-lg flex items-center justify-center"
              >
                <FaMinus size={9} />
              </button>
              <span className="text-sm font-black text-indigo-700 w-6 text-center">
                {qtyInSelection}
              </span>
              <button
                onClick={() => canAdd && onAdd(activeItem)}
                disabled={!canAdd}
                className="w-6 h-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ImPlus size={9} />
              </button>
            </div>
            <span className="text-white bg-green-500 text-xs rounded-lg px-2 py-1">ADDED</span>
          </>
        ) : (
          <button
            onClick={() => canAdd && onAdd(activeItem)}
            disabled={!canAdd}
            className={`p-2 rounded-lg transition-all ${
              !canAdd
                ? "text-gray-200 cursor-not-allowed"
                : "text-gray-600 hover:bg-indigo-50 hover:text-indigo-700 active:scale-95"
            }`}
          >
            <ImPlus size={15} />
          </button>
        )}
      </td>
    </tr>
  );
};

// --- MAIN COMPONENT ---
const AddWarehouseRequest = ({ onClose, refreshData, requestToEdit }) => {
  const currentUserID = useSelector(selectUserID);
  const isEditMode = !!requestToEdit;

  const [step, setStep] = useState(isEditMode ? 2 : 1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingBase, setIsFetchingBase] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);

  // Reference Data
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [approvers, setApprovers] = useState([]);

  // Selections
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSourceValue, setSelectedSourceValue] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Form Details
  const [formData, setFormData] = useState({
    warehouseId: "",
    approverId: "",
    notes: "",
  });

  // 1. Fetch Lookups
  useEffect(() => {
    const fetchBaseData = async () => {
      setIsFetchingBase(true);
      try {
        const [wRes, aRes, locRes] = await Promise.all([
          axios.get(`${domain}/api/Warehouses`),
          axios.get(`${domain}/api/Approvers`),
          axios.get(`${domain}/api/Locations`),
        ]);
        setWarehouses(wRes.data || []);
        setApprovers(aRes.data || []);
        setLocations(locRes.data || []);
      } catch (error) {
        toast.error("Failed to load form data.");
      } finally {
        setIsFetchingBase(false);
      }
    };
    fetchBaseData();
  }, []);

  // --- EDIT MODE INITIALIZATION (Fix for Issue 3) ---
  useEffect(() => {
    if (
      isEditMode &&
      requestToEdit &&
      warehouses.length > 0 &&
      locations.length > 0
    ) {
      const {
        originLocationId,
        originWarehouseId,
        warehouseId,
        approverId,
        notes,
        items,
      } = requestToEdit;

      const sourceVal = originLocationId
        ? `loc-${originLocationId}`
        : originWarehouseId
          ? `wh-${originWarehouseId}`
          : "";
      setSelectedSourceValue(sourceVal);

      // Clean the automatic "(Transferring from Warehouse: ...)" note from edit mode to keep it tidy
      let cleanNotes = notes || "";
      cleanNotes = cleanNotes.replace(
        /^\(Transferring from Warehouse: [^)]+\)\s*/,
        "",
      );

      setFormData({
        warehouseId: warehouseId?.toString() || "",
        approverId: approverId?.toString() || "",
        notes: cleanNotes,
      });

      let sourceName = "Unknown";
      if (originLocationId)
        sourceName =
          locations.find((l) => l.id === originLocationId)?.locationName ||
          sourceName;
      if (originWarehouseId)
        sourceName =
          warehouses.find((w) => w.id === originWarehouseId)?.warehouseName ||
          sourceName;

      if (items && items.length > 0) {
        const mapped = items.map((i) => {
          const uid = i.uniqueId || `wh-item-${i.productId}`;
          return {
            id: i.productId,
            productId: i.productId,
            uniqueId: uid,
            cartKey: `${uid}-${sourceVal}`,
            uomId: i.uomId || null,
            productName: i.productName,
            brandName: i.brandName,
            itemCode: i.itemCode,
            uomName: i.uomName || "Unit",
            quantity: i.requestedQuantity,
            maxQty: 99999, // Allow flexibility during edit mode
            originType: originLocationId ? "loc" : "wh",
            originId: originLocationId || originWarehouseId,
            originName: sourceName,
          };
        });
        setSelectedItems(mapped);
      }
    }
  }, [isEditMode, requestToEdit, warehouses, locations]);

  // 2. Fetch Products based on the Source selected
  useEffect(() => {
    if (!selectedSourceValue) return;

    const fetchSourceProducts = async () => {
      setIsLoadingProducts(true);
      try {
        const [type, id] = selectedSourceValue.split("-");

        if (type === "loc") {
          const res = await axios.get(
            //`${domain}/api/Products/pos-pricelist-by-location?locationId=${id}`,
            `${domain}/api/Products/stock-by-location/${id}`,
          );
          const normalized = res.data.map((p) => ({
            id: p.id,
            productId: p.productId || p.id,
            uniqueId:
              p.uniqueId ||
              `${p.productId || p.id}-${p.uomId || Math.random()}`,
            uomId: p.uomId,
            productName: p.productName,
            brandName: p.brandName,
            itemCode: p.itemCode,
            uomName: p.uom?.uomCode || p.uomName || "Unit",
            stockCount: p.stockCount,
            sourceType: "Location",
          }));
          setProducts(normalized);
        } else if (type === "wh") {
          const res = await axios.get(`${domain}/api/WarehouseStock/detail`);
          const warehouseData = res.data.find(
            (w) => String(w.warehouseId) === String(id),
          );

          if (warehouseData && warehouseData.stockItems) {
            const normalized = warehouseData.stockItems.map((p) => ({
              id: p.productId,
              productId: p.productId,
              uniqueId: `wh-item-${p.productId}`,
              uomId: null,
              productName: p.productName,
              brandName: p.brandName,
              itemCode: p.itemCode,
              uomName: p.uomName || "Unit",
              stockCount: p.currentQuantity,
              sourceType: "Warehouse",
            }));
            setProducts(normalized);
          } else {
            setProducts([]);
            toast.info("No items currently tracked in this warehouse.");
          }
        }
      } catch {
        toast.error("Failed to load products for this source.");
        setProducts([]);
      } finally {
        setIsLoadingProducts(false);
      }
    };
    fetchSourceProducts();
  }, [selectedSourceValue]);

  const groupedProducts = useMemo(() => {
    const filtered = searchQuery.trim()
      ? products.filter(
          (item) =>
            (item.productName &&
              item.productName
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (item.itemCode &&
              item.itemCode.toLowerCase().includes(searchQuery.toLowerCase())),
        )
      : products;

    return Object.values(
      filtered.reduce((acc, item) => {
        const key = item.productId;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {}),
    );
  }, [products, searchQuery]);

  const handleAdd = useCallback(
    (activeItem) => {
      setSelectedItems((prev) => {
        const cartKey = `${activeItem.uniqueId}-${selectedSourceValue}`;
        const existing = prev.find((i) => i.cartKey === cartKey);

        if (existing) {
          if (
            activeItem.stockCount != null &&
            existing.quantity >= activeItem.stockCount
          ) {
            toast.error(
              `Max stock (${activeItem.stockCount}) reached from this source.`,
            );
            return prev;
          }
          return prev.map((i) =>
            i.cartKey === cartKey ? { ...i, quantity: i.quantity + 1 } : i,
          );
        }

        let sourceName = "Unknown";
        const [type, id] = selectedSourceValue.split("-");
        if (type === "loc")
          sourceName = locations.find((l) => String(l.id) === id)?.locationName;
        if (type === "wh")
          sourceName = warehouses.find(
            (w) => String(w.id) === id,
          )?.warehouseName;

        return [
          ...prev,
          {
            id: activeItem.id,
            productId: activeItem.productId || activeItem.id,
            uniqueId: activeItem.uniqueId,
            cartKey: cartKey,
            uomId: activeItem.uomId,
            productName: activeItem.productName,
            brandName: activeItem.brandName,
            itemCode: activeItem.itemCode,
            uomName: activeItem.uomName,
            quantity: 1,
            maxQty: activeItem.stockCount,
            originType: type,
            originId: id,
            originName: sourceName,
          },
        ];
      });
    },
    [selectedSourceValue, locations, warehouses],
  );

  const handleRemove = useCallback(
    (activeItem) => {
      setSelectedItems((prev) => {
        const cartKey = `${activeItem.uniqueId}-${selectedSourceValue}`;
        const existing = prev.find((i) => i.cartKey === cartKey);
        if (!existing) return prev;
        if (existing.quantity <= 1)
          return prev.filter((i) => i.cartKey !== cartKey);
        return prev.map((i) =>
          i.cartKey === cartKey ? { ...i, quantity: i.quantity - 1 } : i,
        );
      });
    },
    [selectedSourceValue],
  );

  // SAVE / SUBMIT LOGIC
  const handleSave = async () => {
    if (!formData.warehouseId)
      return toast.error("Select a Destination Warehouse.");
    if (!formData.approverId) return toast.error("Select an Approver.");

    // --- Validation (Issue 4): Stop circular warehouse transfers ---
    const circularViolation = selectedItems.some(
      (i) =>
        i.originType === "wh" &&
        String(i.originId) === String(formData.warehouseId),
    );
    if (circularViolation) {
      return toast.error(
        "Destination Warehouse cannot be the same as the Source Warehouse.",
      );
    }

    setIsLoading(true);

    try {
      // --- EDIT MODE (Issue 3 Implementation) ---
      if (isEditMode) {
        // Enforce single source for PUT request
        const uniqueSources = [
          ...new Set(selectedItems.map((i) => `${i.originType}-${i.originId}`)),
        ];
        if (uniqueSources.length > 1) {
          setIsLoading(false);
          return toast.error(
            "Edit mode only supports a single source. Please submit items separately.",
          );
        }

        const [type, sourceId] = uniqueSources[0].split("-");
        const payload = {
          warehouseId: parseInt(formData.warehouseId),
          approverId: parseInt(formData.approverId),
          notes: formData.notes,
          userID: currentUserID,
          originLocationId: type === "loc" ? parseInt(sourceId) : null,
          originWarehouseId: type === "wh" ? parseInt(sourceId) : null,
          items: selectedItems.map((i) => ({
            productId: parseInt(i.productId),
            uomId: i.uomId ? parseInt(i.uomId) : undefined,
            requestedQuantity: parseFloat(i.quantity),
          })),
        };

        await axios.put(
          `${domain}/api/WarehouseRequests/${requestToEdit.id}`,
          payload,
        );
        toast.success("Transfer request updated successfully.");
      }
      // --- CREATE MODE ---
      else {
        const itemsBySource = selectedItems.reduce((acc, item) => {
          const sourceKey = `${item.originType}-${item.originId}`;
          if (!acc[sourceKey]) acc[sourceKey] = [];
          acc[sourceKey].push(item);
          return acc;
        }, {});

        const promises = Object.keys(itemsBySource).map((sourceKey) => {
          const [type, sourceId] = sourceKey.split("-");

          const payload = {
            warehouseId: parseInt(formData.warehouseId),
            approverId: parseInt(formData.approverId),
            notes: formData.notes,
            userID: currentUserID,
            items: itemsBySource[sourceKey].map((i) => ({
              productId: parseInt(i.productId),
              uomId: i.uomId ? parseInt(i.uomId) : undefined,
              requestedQuantity: parseFloat(i.quantity),
            })),
          };

          if (type === "loc") {
            payload.originLocationId = parseInt(sourceId);
          } else if (type === "wh") {
            payload.originWarehouseId = parseInt(sourceId);
            payload.notes =
              `(Transferring from Warehouse: ${itemsBySource[sourceKey][0].originName}) ` +
              payload.notes;
          }

          return axios.post(`${domain}/api/WarehouseRequests`, payload);
        });

        await Promise.all(promises);
        toast.success(
          `Successfully created ${Object.keys(itemsBySource).length} transfer request(s).`,
        );
      }

      if (refreshData) refreshData();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save request.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/60 backdrop-blur-sm p-4">
      <div className="flex h-full max-h-[95vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden border border-gray-200">
        {/* HEADER */}
        <div className="flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
          <div className="flex items-center gap-4">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl text-white shadow-lg transition-all bg-indigo-600 shadow-indigo-200`}
            >
              {step === 1 ? <FaBoxOpen size={18} /> : <Warehouse size={18} />}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 leading-none">
                {step === 1 ? "Select Source & Products" : "Select Destination"}
              </h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                {isEditMode
                  ? "Edit Transfer Request"
                  : "Warehouse Transfer Request"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <StepIndicator step={step} isEditMode={false} />
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-red-500 transition-colors bg-gray-50 hover:bg-red-50 rounded-lg"
            >
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-grow overflow-hidden flex flex-col bg-gray-50/30">
          {step === 1 && isFetchingBase ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 size={32} className="text-indigo-500 animate-spin" />
            </div>
          ) : (
            <div
              className={`flex-1 overflow-hidden ${step === 1 ? "flex flex-col" : "overflow-y-auto"} px-6 py-6`}
            >
              {/* STEP 1: SOURCE SELECTION */}
              {step === 1 && (
                <div className="flex flex-col flex-1 overflow-hidden gap-4">
                  <div className="flex flex-col sm:flex-row gap-3 shrink-0">
                    <div className="relative flex-1">
                      <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={15}
                      />
                      <input
                        type="text"
                        className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 shadow-sm"
                        placeholder="Search items..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        disabled={!selectedSourceValue || isLoadingProducts}
                      />
                    </div>

                    <div className="relative sm:w-80">
                      <Server
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        size={14}
                      />
                      <select
                        value={selectedSourceValue}
                        onChange={(e) => setSelectedSourceValue(e.target.value)}
                        disabled={isEditMode} // Cannot switch primary source cleanly in Edit mode
                        className={`w-full pl-9 pr-8 py-2.5 text-sm font-semibold rounded-lg focus:outline-none shadow-sm appearance-none cursor-pointer border ${
                          !selectedSourceValue
                            ? "border-amber-300 text-amber-700 bg-amber-50"
                            : "border-indigo-300 text-indigo-800 bg-indigo-50"
                        } ${isEditMode ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        <option value="" disabled>
                          — Select Source —
                        </option>
                        <optgroup label="Physical Locations (POS)">
                          {locations.map((loc) => (
                            <option
                              key={`loc-${loc.id}`}
                              value={`loc-${loc.id}`}
                            >
                              📍 {loc.locationName}
                            </option>
                          ))}
                        </optgroup>
                        <optgroup label="Other Warehouses (Storage)">
                          {warehouses.map((w) => (
                            <option key={`wh-${w.id}`} value={`wh-${w.id}`}>
                              🏭 {w.warehouseName}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                      <ChevronDown
                        size={13}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                      />
                    </div>
                  </div>

                  {!selectedSourceValue && (
                    <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 shrink-0">
                      <AlertCircle
                        className="text-amber-500 shrink-0"
                        size={16}
                      />
                      <p className="text-sm text-amber-800 font-semibold">
                        Select a Source (Location or Warehouse) to load
                        available stock.
                      </p>
                    </div>
                  )}

                  {selectedSourceValue && isLoadingProducts && (
                    <div className="flex-1 flex items-center justify-center">
                      <Loader2
                        size={30}
                        className="text-indigo-400 animate-spin"
                      />
                    </div>
                  )}

                  {selectedSourceValue && !isLoadingProducts && (
                    <>
                      {/* DESKTOP TABLE VIEW */}
                      <div className="hidden md:block flex-1 overflow-auto rounded-xl border border-gray-200 shadow-sm">
                        <table className="w-full bg-white">
                          <thead className="sticky top-0 z-10">
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-left uppercase">
                                Product
                              </th>
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-center uppercase">
                                Brand
                              </th>
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-center uppercase">
                                Unit
                              </th>
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-center uppercase">
                                Stock at Source
                              </th>
                              <th className="py-2 px-6 text-xs font-black text-gray-400 text-center uppercase w-32">
                                Add
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {groupedProducts.map((group) => (
                              <ProductRow
                                key={group[0].productId}
                                group={group}
                                selectedItems={selectedItems}
                                onAdd={handleAdd}
                                onRemove={handleRemove}
                                activeSourceId={selectedSourceValue}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* MOBILE CARD VIEW (Fix for Issue 2) */}
                      <div className="md:hidden flex flex-col gap-3 overflow-y-auto pb-4">
                        {groupedProducts.map((group) => (
                          <MobileProductCard
                            key={group[0].productId}
                            group={group}
                            selectedItems={selectedItems}
                            onAdd={handleAdd}
                            onRemove={handleRemove}
                            activeSourceId={selectedSourceValue}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* STEP 2: DETAILS FORM */}
              {step === 2 && (
                <div className="space-y-6">
                  <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="sm:col-span-1">
                        <label className={labelClass}>
                          Destination Warehouse{" "}
                          <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <Warehouse
                            size={13}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                          />
                          <select
                            value={formData.warehouseId}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                warehouseId: e.target.value,
                              }))
                            }
                            className={`${inputClass} pl-8 appearance-none cursor-pointer border-green-300 bg-green-50 focus:border-green-500`}
                          >
                            <option value="">— Select Destination —</option>
                            {warehouses.map((w) => {
                              // Validation (Issue 4 Display Setup): Disable option if it is selected as the source warehouse!
                              const isSource = selectedItems.some(
                                (i) =>
                                  i.originType === "wh" &&
                                  String(i.originId) === String(w.id),
                              );
                              return (
                                <option
                                  key={w.id}
                                  value={w.id}
                                  disabled={isSource}
                                >
                                  {w.warehouseName}{" "}
                                  {isSource ? " (Current Source)" : ""}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      </div>

                      <div className="sm:col-span-1">
                        <label className={labelClass}>
                          Approver <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.approverId}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              approverId: e.target.value,
                            }))
                          }
                          className={inputClass}
                        >
                          <option value="">— Select Approver —</option>
                          {approvers.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.employeeName && a.employeeName !== "N/A"
                                ? a.employeeName
                                : a.userName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* NOTES FIELD (Fix for Issue 1 verification - explicitly present) */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <label className={labelClass}>
                        Transfer Notes / Reason{" "}
                        <span className="text-gray-400">(optional)</span>
                      </label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, notes: e.target.value }))
                        }
                        rows={3}
                        className={`${inputClass} resize-none`}
                        placeholder="Add any additional notes or special instructions for this transfer request..."
                      />
                    </div>
                  </div>

                  <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
                    <table className="min-w-full divide-y divide-gray-100">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-[10px] font-black uppercase text-gray-400">
                            Product
                          </th>
                          <th className="px-6 py-3 text-left text-[10px] font-black uppercase text-gray-400">
                            Brand
                          </th>
                          <th className="px-6 py-3 text-left text-[10px] font-black uppercase text-gray-400">
                            Pulling From (Source)
                          </th>
                          <th className="px-6 py-3 text-center text-[10px] font-black uppercase text-gray-400">
                            Stocks
                          </th>
                          <th className="px-6 py-3 text-center text-[10px] font-black uppercase text-gray-400 w-32">
                            Qty
                          </th>
                          <th className="px-6 py-3 text-right text-[10px] font-black uppercase text-gray-400 w-24">
                            Action
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedItems.map((item) => {
                          const foundPricelist = products.find(product => product.productId === item.productId);
                          return (
                            <tr
                              key={item.cartKey}
                              className="hover:bg-gray-50/50"
                            >
                              <td className="px-6 py-3">
                                <p className="text-sm font-bold text-gray-800">
                                  {item.productName}
                                </p>
                                <p className="text-[10px] font-bold text-indigo-500 uppercase">
                                  {item.itemCode ? `${item.itemCode} · ` : ""}
                                  {item.uomName}
                                </p>
                              </td>
                              <td className="py-2 px-6">
                                {item.brandName ?
                                <>
                                  <span className="text-xs">{item.brandName}</span>
                                </>
                                :
                                <>
                                  <span className="text-xs italic text-gray-400">{"N/A"}</span>
                                </>
                                }
                              </td>
                              <td className="px-6 py-3">
                                <span
                                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold shadow-sm ${
                                    item.originType === "loc"
                                      ? "bg-indigo-50 border-indigo-200 text-indigo-800"
                                      : "bg-emerald-50 border-emerald-200 text-emerald-800"
                                  }`}
                                >
                                  {item.originType === "loc" ? "📍" : "🏭"}{" "}
                                  {item.originName}
                                </span>
                              </td>
                              <td className="px-6 py-3 text-center"> {/* Added text-center here */}
                                <p className="text-sm font-bold text-gray-500">
                                  {foundPricelist?.stockCount ?? "0"} 
                                </p>
                              </td>
                              <td className="px-6 py-3">
                                <input
                                  type="number"
                                  min="0.01"
                                  step="any"
                                  value={item.quantity}
                                  onChange={(e) => {
                                    const newQty = e.target.value;
                                    setSelectedItems((prev) =>
                                      prev.map((i) =>
                                        i.cartKey === item.cartKey
                                          ? { ...i, quantity: newQty }
                                          : i,
                                      ),
                                    );
                                  }}
                                  className="w-full rounded-md border border-gray-300 bg-white py-1.5 px-2 text-center text-sm font-bold focus:ring-indigo-500 outline-none shadow-sm"
                                />
                              </td>
                              <td className="px-6 py-3 text-right">
                                <button
                                  onClick={() =>
                                    setSelectedItems((prev) =>
                                      prev.filter(
                                        (i) => i.cartKey !== item.cartKey,
                                      ),
                                    )
                                  }
                                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                                >
                                  <FaTrash size={13} />
                                </button>
                              </td>
                            </tr>
                          )})}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex items-center justify-between border-t border-gray-100 bg-white px-6 py-4">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors px-4 py-2"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                disabled={selectedItems.length === 0}
                className="flex items-center gap-2 rounded-xl bg-indigo-600 px-8 py-3 text-sm font-black text-white shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-40"
              >
                Go to Destination <FaArrowRight size={12} />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                className="text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors px-4 py-2"
              >
                ← Add More Items
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex items-center gap-2 rounded-xl bg-green-600 px-8 py-3 text-sm font-black text-white shadow-lg shadow-green-100 hover:bg-green-700 transition-all active:scale-95 disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={16} />
                ) : (
                  <FaSave size={13} />
                )}
                {isEditMode ? "Update Request" : "Submit Request"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddWarehouseRequest;
